/**
 * Remembers where the reader was inside a rendered document, so a page load puts
 * them back there instead of at the top.
 *
 * A LODA hotline manual is hundreds of pages; losing the position costs real time
 * mid-call. Any full page load throws it away — a browser-discarded tab, a deploy
 * reload, or the full-page redirect an expired session forces (LAR-361). Kept in
 * `sessionStorage`, which survives all three but does not leak between tabs.
 *
 * Mirrors {@link file://./chatScrollMemory.ts}, minus the "was at the bottom"
 * handling: a document has a fixed height, so a raw offset restores faithfully.
 */
export type DocumentScrollState = {
  scrollTop: number;
};

const scrollStateKey = (documentKey: string): string =>
  `document:scrollPosition:${documentKey}`;

/** Below this, restoring is indistinguishable from the top and just adds a jump. */
const MIN_PERSISTED_SCROLL_TOP = 24;

/**
 * Takes the offset rather than the element on purpose: the last moment worth
 * saving is often after React has detached the document, when reading a live
 * container yields the surviving page shell's `scrollTop` of 0 and silently
 * erases the memory.
 */
export const saveDocumentScrollState = (
  documentKey: string,
  scrollTop: number
): void => {
  if (typeof sessionStorage === "undefined") return;
  if (!Number.isFinite(scrollTop)) return;

  const key = scrollStateKey(documentKey);

  try {
    if (scrollTop < MIN_PERSISTED_SCROLL_TOP) {
      sessionStorage.removeItem(key);
      return;
    }
    const state: DocumentScrollState = { scrollTop };
    sessionStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* private mode / quota — losing the position is not worth throwing over */
  }
};

export const getDocumentScrollState = (
  documentKey: string
): DocumentScrollState | null => {
  if (typeof sessionStorage === "undefined") return null;

  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(scrollStateKey(documentKey));
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "scrollTop" in parsed &&
      typeof (parsed as DocumentScrollState).scrollTop === "number" &&
      Number.isFinite((parsed as DocumentScrollState).scrollTop)
    ) {
      return { scrollTop: (parsed as DocumentScrollState).scrollTop };
    }
  } catch {
    /* malformed entry — treat as no memory */
  }

  return null;
};

export const clearDocumentScrollState = (documentKey: string): void => {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(scrollStateKey(documentKey));
  } catch {
    /* private mode / quota */
  }
};

/**
 * Restoring past the end of a shorter render (a document that changed, or one
 * whose images have not laid out yet) would silently land at the bottom.
 */
export const resolveRestoredScrollTop = (
  saved: DocumentScrollState,
  container: HTMLElement
): number => {
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  return Math.min(saved.scrollTop, maxScrollTop);
};
